// asciicast v2 parsing.
//
// These recordings are NOT video. The file is newline-delimited JSON: one
// header object, then one array per event.
//
//   {"version":2,"width":80,"height":24,"timestamp":1790223816,
//    "title":"ubuntu@10.0.0.5:22","session_id":"d7bbae886dcfe00d",
//    "principal":"alice@corp.com","key_fingerprint":"SHA256:1SUi…",
//    "target_user":"ubuntu","source_ip":"10.2.14.7"}
//   [0.171438,"m","shell: "]
//   [0.173458,"o","Welcome to Ubuntu 22.04.5 LTS\r\n"]
//   [1.760429,"i","w"]
//
// Event tuple is [secondsSinceStart, type, data].
export const EVENT_OUTPUT = "o"; // output from the target — this is what renders
export const EVENT_INPUT = "i"; // bytes the operator typed
export const EVENT_MARKER = "m"; // metadata; an annotation, not terminal text

export const DEFAULT_COLS = 80;
export const DEFAULT_ROWS = 24;

// A marker whose text says the proxy withheld what was typed. The proxy
// withholds typed bytes it never saw echoed back — which is exactly what a
// password at a sudo prompt looks like, because the remote side turns echo off
// and no packet announces it. The marker is what keeps the gap explained rather
// than silent, so it has to be rendered, not filtered.
const REDACTION_HINT = /(redact|withheld|suppress|hidden|no-echo|noecho|masked)/i;

export function isRedactionMarker(text) {
  return REDACTION_HINT.test(String(text || ""));
}

// → { header, events, errors }
// Tolerant on purpose: one unparseable line in the middle of a session should
// cost that line, not the whole recording.
export function parseAsciicast(text) {
  const out = { header: null, events: [], errors: [] };
  if (!text) return out;

  const lines = String(text).split("\n");
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (!line) continue;
    let parsed;
    try {
      parsed = JSON.parse(line);
    } catch {
      out.errors.push({ line: i + 1, reason: "not JSON" });
      continue;
    }
    if (!out.header && parsed && !Array.isArray(parsed) && typeof parsed === "object") {
      out.header = parsed;
      continue;
    }
    if (Array.isArray(parsed) && parsed.length >= 3) {
      const [time, type, data] = parsed;
      const t = Number(time);
      if (!Number.isFinite(t)) {
        out.errors.push({ line: i + 1, reason: "bad timestamp" });
        continue;
      }
      out.events.push({ time: t, type: String(type), data: String(data ?? "") });
    } else {
      out.errors.push({ line: i + 1, reason: "not an event tuple" });
    }
  }

  // Timestamps are already monotonic in a well-formed file; sorting costs
  // nothing on files this size and protects the scrubber from a bad one.
  out.events.sort((a, b) => a.time - b.time);
  return out;
}

export function castDuration(events) {
  return events.length ? events[events.length - 1].time : 0;
}

export function castCols(header) {
  const n = Number(header?.width);
  return Number.isFinite(n) && n > 0 ? Math.min(n, 400) : DEFAULT_COLS;
}

export function castRows(header) {
  const n = Number(header?.height);
  return Number.isFinite(n) && n > 0 ? Math.min(n, 200) : DEFAULT_ROWS;
}

// Under a terminal the target echoes back what was typed, so `i` events usually
// duplicate `o` events one-for-one. Rendering both doubles every character —
// that looks like a bug and is not, which is why output-only is the default and
// keystrokes are an explicit opt-in.
export function selectPlayableEvents(events, { showKeystrokes = false } = {}) {
  return events.filter((e) => e.type === EVENT_OUTPUT || (showKeystrokes && e.type === EVENT_INPUT));
}

export function selectMarkers(events) {
  return events.filter((e) => e.type === EVENT_MARKER);
}

export function formatClock(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return `${String(m).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}
